// Path: ./src/app/pages/UserPage/index.tsx
import React, { useEffect, useState } from "react";
import { AccountCard } from "../../components/AccountCard";
import { useNavigate } from "react-router-dom";
import { adminApiService } from "../../../utils/api";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import {
  GfAdminUser,
  GfAdminUserDto,
  GfPermissionDto,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";

import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { initialGridPaginationModel } from "../../../utils/constant/initialGridPaginationModel";
import { initialGridSortModel } from "../../../utils/constant/initialGridSortModel";
import { TableData } from "../../../utils/interface/tableData";
import { useAdminUserTableColumns } from "./useAdminUserTableColumns";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { routes } from "../../../utils/constant/navigationRoutes";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

interface roleInfoProps {
  roleName: string;
  listItems: {
    title: string;
    itemsList: string[];
  }[];
}

const UserPage = () => {
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();
  const navigate = useNavigate();
  // const { setIsTableLoading } = useLoading(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [userData, setUserData] = useState<TableData<GfAdminUser>>({
    columns: [],
    rows: [],
    total: 0,
  });
  const [roleInfo, setRoleInfo] = useState<roleInfoProps>({
    roleName: "",
    listItems: [],
  });
  const [open, setOpen] = useState(false);
  const [deleteUserData, setDeleteUserData] = useState<
    GfAdminUserDto | undefined
  >();
  const [pagination, setPagination] = useState<GridPaginationModel>(
    initialGridPaginationModel,
  );
  const [sortModel, setSortModel] =
    React.useState<GridSortModel>(initialGridSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  /**
   * Handles the click event and navigates to the user creation page.
   */
  const handleClick = () => {
    navigate(routes.user.create);
  };

  const AccountCardData = {
    cardTitle: "User",
    buttonText: t(messages.Add_User()),
    permissionList: {
      title: t(messages.Role()),
      subTitle: roleInfo.roleName,
      bottomTitle: t(messages.Permission()),
      listItems: roleInfo.listItems,
    },
    /**
     * Handles the click event and navigates to the user creation page.
     */
    onClick: handleClick,
  };

  /**
   * Opens a confirmation modal to delete a User.
   *
   * @param value - The User data to be deleted.
   */
  const handleOpenDelete = (value: GfAdminUserDto) => {
    setOpen(true);
    setDeleteUserData(value);
  };
  const columns = useAdminUserTableColumns(handleOpenDelete);
  const handleCancel = () => {
    setOpen(false);
  };

  const handleOnDelete = async () => {
    setIsTableLoading(true);
    try {
      deleteUserData?.id &&
        (await adminApiService.deleteOneBaseGfAdminUserAdminControllerGfAdminUser(
          deleteUserData?.id,
          authHeaders,
        ));

      toastSuccess(t(messages.User_Deleted_SuccessFully()));
    } catch (error) {
      console.log(error);
      toastError(t(messages.Error_occurred_while_deleting()));
    }

    handleCancel();
    setDeleteUserData(undefined);
    if (muiFilterModelHasActiveItems(filterModel)) {
      await fetchUsersWithFilter(filterModel);
    } else {
      await fetchUsers(pagination, sortModel);
    }
    setIsTableLoading(false);
  };

  const fetchUsersWithFilter = async (activeFilterModel: GridFilterModel) => {
    await guardedListFetch(
      listRequestGuard,
      setIsTableLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfAdminUserAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setUserData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
      (error) => {
        console.log(error);
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchUsers = async (
    paginationModel: GridPaginationModel,
    gridSortModel: GridSortModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setIsTableLoading,
      async () =>
        adminApiService.getManyBaseGfAdminUserAdminControllerGfAdminUser(
          undefined,
          undefined,
          undefined,
          undefined,
          gridSortModel?.length
            ? [
                `${
                  gridSortModel[0].field
                },${gridSortModel[0]?.sort?.toUpperCase()}`,
              ]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel.pageSize,
          0,
          paginationModel.page,
          undefined,
          authHeaders,
        ),
      (result) => {
        setUserData((prev) => ({
          ...prev,
          rows: result?.data, //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) there is a bug here
          total: result?.total,
        }));
      },
    );
  };

  interface RowProps extends GfAdminUserDto {
    roleId: string;
  }
  interface handleOnRowClick {
    columns: [];
    id: string;
    row: RowProps;
  }

  interface ObjectTypes {
    key: string[];
    value: boolean;
  }

  interface accProps {
    coupon?: ObjectTypes[]; //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) not normal to me
    deals?: ObjectTypes[];
    flyer?: ObjectTypes[];
    tag?: ObjectTypes[];
    upload?: ObjectTypes[];
    user?: ObjectTypes[];
  }

  const handleOnRowClick = async (value?: handleOnRowClick) => {
    if (value?.row) {
      if (value?.row?.roleId) {
        setIsTableLoading(true);
        const result =
          await adminApiService.getOneBaseGfRoleAdminControllerGfRole(
            value.row.roleId,
          );
        const groups = result?.permissions?.reduce(
          (acc: accProps, obj: GfPermissionDto) => {
            const group = obj.name.split("-")[0] as keyof accProps;
            if (!acc[group]) {
              acc[group] = [];
            }
            acc[group]?.push({
              key: [obj.name.split("-")[1]],
              value: false,
            });
            return acc;
          },
          {},
        );

        const groupArray = Object.entries(groups).map(([key, value]) => ({
          group: key,
          objects: value as ObjectTypes[],
        }));

        const ItemList = groupArray?.map((value) => {
          return {
            title: value.group,
            itemsList: value?.objects?.map((val) => val?.key[0]),
          };
        });

        setRoleInfo({
          roleName: result.name,
          listItems: ItemList,
        });
        setIsTableLoading(false);
      } else {
        setRoleInfo({
          roleName: "",
          listItems: [],
        });
      }
    } else {
      setRoleInfo({
        roleName: "",
        listItems: [],
      });
    }
  };

  useEffect(() => {
    setUserData({
      columns: columns,
    });
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchUsersWithFilter(filterModel);
    } else {
      fetchUsers(pagination, sortModel);
    }
  }, [pagination, sortModel, filterModel]);

  return (
    <SetupPageContainer data-testid="user-view-page">
      <AccountCard
        loadFailed={listRequestGuard.loadFailed}
        permissionGroupName="user"
        permissionRoleName="create"
        isLoading={isTableLoading}
        cardTitle={AccountCardData.cardTitle}
        buttonText={AccountCardData.buttonText}
        onClick={AccountCardData.onClick}
        permissionList={AccountCardData.permissionList}
        rolesData={userData}
        onRowClick={handleOnRowClick}
        initialPaginationModel={pagination}
        initialSortModel={sortModel}
        onTableChange={(
          gridPaginationModel: GridPaginationModel,
          gridSortModel: GridSortModel,
        ) => {
          setPagination(gridPaginationModel);
          setSortModel(gridSortModel);
        }}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        buttonDataTestId={"add-user-button"}
      />
      <MuiConfirmationModal
        open={open}
        handleCancel={handleCancel}
        handleConfirm={handleOnDelete}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`
      ${t(messages.Are_you_sure_you_want_to_delete_This())}${t(
        messages.User(),
      )} "${deleteUserData?.userName}"? ${t(
        messages.This_action_cannot_be_undone(),
      )}.
      `}
      />
    </SetupPageContainer>
  );
};

export default UserPage;
